import React from "react";
import * as recipeService from "@beers/services/recipeService";
import { recipeFitsKeg, fermentationSummary } from "../recipeFermentation";
import "./recipe-picker.scss";

const TABS = { MINE: "mine", COMMUNITY: "community" };

// A namespaced modifier — NOT the bare `active` class. The app has a global
// `.active { color: white !important }` (styles.scss, meant for nav items) that
// would otherwise force white text on the selected tab (white-on-white on the
// white modal). Scoping the active class avoids that collision without fighting
// the global !important.
const ACTIVE_TAB = "recipe-picker__tab--active";

class RecipePicker extends React.Component {
  state = {
    tab: TABS.MINE,
    beers: [],
    communityResults: [],
    communityTerm: "",
    selected: null,
    eligibility: null,
    loading: true,
    error: null,
  };

  componentDidMount() {
    // Scope "Mine" to the caller's own beers via ?brewer=<id> — the same call
    // the My Beers page (BeerList) and BrewModal make. The bare /beers/
    // endpoint also returns the whole store catalog (every brewpack beer has
    // show_in_minibrew_app=true), so without the filter published brewpacks
    // leak into this list; their recipes are then owner-filtered out of
    // /recipes/, so picking one dead-ends on "Could not load that recipe".
    const { brewerId } = this.props;
    if (brewerId == null) {
      // No caller id — show an empty Mine tab rather than the full catalog.
      this.setState({ beers: [], loading: false });
      return;
    }
    recipeService
      .getBeers(brewerId)
      .then((beers) =>
        this.setState({
          beers: (beers || []).slice().sort(byName),
          loading: false,
        }),
      )
      .catch(() =>
        this.setState({
          error: "Could not load your recipes.",
          loading: false,
        }),
      );
  }

  // Resolve a beer -> its latest recipe version -> full recipe detail.
  // Sequence-guarded (mirrors _searchSeq below) so a slow pick can't resolve
  // after — and overwrite — a later, faster pick.
  pickBeer = async (beer) => {
    const seq = (this._pickSeq || 0) + 1;
    this._pickSeq = seq;
    this.setState({ loading: true, error: null });
    try {
      const versions = (await recipeService.getRecipes(beer.id)) || [];
      const latest = versions
        .slice()
        .sort((a, b) => Number(b.version_name) - Number(a.version_name))[0];
      if (!latest) throw new Error("no versions");
      const recipe = await recipeService.getRecipeById(latest.id);
      if (seq !== this._pickSeq) return; // a newer pick superseded this one
      this.select(recipe);
    } catch (e) {
      if (seq === this._pickSeq)
        this.setState({ loading: false, error: "Could not load that recipe." });
    }
  };

  pickCommunity = async (row) => {
    const seq = (this._pickSeq || 0) + 1;
    this._pickSeq = seq;
    this.setState({ loading: true, error: null });
    try {
      const recipe = await recipeService.getSharedRecipe(row.id);
      if (seq !== this._pickSeq) return; // a newer pick superseded this one
      this.select(recipe);
    } catch (e) {
      if (seq === this._pickSeq)
        this.setState({ loading: false, error: "Could not load that recipe." });
    }
  };

  select(recipe) {
    const eligibility = recipeFitsKeg(recipe);
    this.setState({ selected: recipe, eligibility, loading: false });
    this.props.onSelect(eligibility.ok ? recipe : null);
  }

  searchCommunity = (term) => {
    this.setState({ communityTerm: term });
    if (!term) return this.setState({ communityResults: [] });
    const seq = (this._searchSeq || 0) + 1;
    this._searchSeq = seq;
    recipeService
      .searchSharedRecipe(term)
      .then((res) => {
        if (seq !== this._searchSeq) return; // a newer search superseded this one
        this.setState({ communityResults: (res && res.results) || res || [] });
      })
      .catch(() => {
        if (seq === this._searchSeq)
          this.setState({ error: "Community search failed." });
      });
  };

  render() {
    const {
      tab,
      beers,
      communityResults,
      communityTerm,
      selected,
      eligibility,
      loading,
      error,
    } = this.state;
    return (
      <div className="recipe-picker">
        <div className="recipe-picker__tabs">
          <button
            className={tab === TABS.MINE ? ACTIVE_TAB : ""}
            onClick={() => this.setState({ tab: TABS.MINE })}
          >
            Mine
          </button>
          <button
            className={tab === TABS.COMMUNITY ? ACTIVE_TAB : ""}
            onClick={() => this.setState({ tab: TABS.COMMUNITY })}
          >
            Community
          </button>
        </div>

        {tab === TABS.MINE ? (
          !loading && beers.length === 0 ? (
            <div className="recipe-picker__empty">
              No saved recipes yet — create one under Beers.
            </div>
          ) : (
            <ul className="recipe-picker__list">
              {beers.map((b) => (
                <li
                  key={b.id}
                  data-testid={`beer-row-${b.id}`}
                  onClick={() => this.pickBeer(b)}
                >
                  {b.name}
                </li>
              ))}
            </ul>
          )
        ) : (
          <div className="recipe-picker__community">
            <input
              type="search"
              placeholder="Search community recipes"
              value={communityTerm}
              onChange={(e) => this.searchCommunity(e.target.value)}
            />
            {communityTerm && communityResults.length === 0 ? (
              <div className="recipe-picker__empty">
                No community recipes match your search.
              </div>
            ) : (
              <ul className="recipe-picker__list">
                {communityResults.map((r) => (
                  <li
                    key={r.id}
                    data-testid={`shared-row-${r.id}`}
                    onClick={() => this.pickCommunity(r)}
                  >
                    {r.beer_name} — v{r.version_name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {loading && <div className="recipe-picker__status">Loading…</div>}
        {error && <div className="recipe-picker__error">{error}</div>}
        {selected && eligibility && (
          <RecipeSummary recipe={selected} eligibility={eligibility} />
        )}
      </div>
    );
  }
}

const byName = (a, b) => (a.name || "").localeCompare(b.name || "");

function RecipeSummary({ recipe, eligibility }) {
  const rows = fermentationSummary(recipe);
  return (
    <div className="recipe-picker__summary">
      <h4>
        {recipe.beer_name} — v{recipe.version_name}
      </h4>
      {!eligibility.ok ? (
        <p className="recipe-picker__ineligible">{eligibility.reason}</p>
      ) : (
        <React.Fragment>
          <ul>
            {rows.map((s, i) => (
              <li key={i}>
                {s.stageName}: {s.temperature}
                {s.endTemperature != null && s.endTemperature !== s.temperature
                  ? `→${s.endTemperature}`
                  : ""}{" "}
                °C · {formatDays(s.durationDays)}
              </li>
            ))}
          </ul>
          <p className="recipe-picker__serving">
            Serving: {Number(recipe.serving_temperature)} °C
          </p>
          <p className="recipe-picker__note">
            The keg runs this recipe's fermentation schedule (temperatures,
            pressure-relief, dry-hop prompts). Mashing and boiling do not apply
            to a keg.
          </p>
        </React.Fragment>
      )}
    </div>
  );
}

// Fermentation step duration arrives from the API in DAYS (see recipeFermentation.js).
function formatDays(days) {
  const d = Number(days) || 0;
  return `${Number.isInteger(d) ? d : d.toFixed(1)} d`;
}

export default RecipePicker;
